import VideoBanner from './VideoBanner';
import SearchWidget from '@/features/search/presentation/components/SearchWidget';
import SquareMenuSection from './SquareMenuSection';
import StripSection from './StripSection';
import CarouselSection from './CarouselSection';
import CollectionSection from './CollectionSection';
import VideoSection from './VideoSection';
import TrendingSection from './TrendingSection';
import GiftCardSection from './GiftCardSection';
import RoundMenuSection from './RoundMenuSection';
import ReelsSection from './ReelsSection';
import RoundMenuImageSection from './RoundMenuImageSection';
import EditorialSection from './EditorialSection';
import RoundIconSection from './RoundIconSection';
import InfoCardSection from './InfoCardSection';
import StaticBannerSection from './StaticBannerSection';
import AnimatedBoxSection from './AnimatedBoxSection';
import FadedBannerSection from './FadedBannerSection';
import ClipCardsSection from './ClipCardsSection';
import SpotlightSection from './SpotlightSection';
import HomeSeoContentSection from './HomeSeoContentSection';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFullyVisible, useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { useIsDesktop, useMediaQuery } from '@/core/hooks/useMediaQuery';

/**
 * Section Registry
 * Maps section keys to their corresponding React components
 */
const sectionRegistry: Record<
  string,
  React.ComponentType<{
    data: Record<string, unknown>;
    sectionKey: string;
    eventLabel?: string;
    priority?: number;
  }>
> = {
  bgBannerSection: VideoBanner,
  searchSection: SearchWidget,
  squareMenuSection: SquareMenuSection,
  stripSection: StripSection,
  carouselSection: CarouselSection,
  collectionSection: CollectionSection,
  videoSection: VideoSection,
  trendingSection: TrendingSection,
  giftCardSection: GiftCardSection,
  roundMenuSection: RoundMenuSection,
  reelsSection: ReelsSection,
  roundMenuImageSection: RoundMenuImageSection,
  editorialSection: EditorialSection,
  roundIconSection: RoundIconSection,
  infoCardSection: InfoCardSection,
  homeSeoContentSection: HomeSeoContentSection,
  staticBannerSection: StaticBannerSection,
  animatedBoxSection: AnimatedBoxSection,
  fadedBannerSection: FadedBannerSection,
  clipCardsSection: ClipCardsSection,
  spotlightSection: SpotlightSection,
  // Add more sections here as they are created
};

/**
 * Section Renderer Component
 * Dynamically renders sections based on their key
 */
interface SectionRendererProps {
  section: {
    priority?: number;
    key: string;
    eventLabel?: string;
    value: Record<string, unknown>;
    showInDesktop?: boolean;
    showInMobile?: boolean;
    viewThreshold?: number; // 0-1, threshold for "Section Viewed" event (default: 0.5; spotlight: 1.0)
  };
}

export default function SectionRenderer({ section }: SectionRendererProps) {
  const { key, value, eventLabel, priority, viewThreshold } = section;
  const { analytics, isInitialized } = useAnalytics();
  const effectiveViewThreshold =
    viewThreshold ??
    (key === 'spotlightSection' ? 1.0 : key === 'editorialSection' ? 0.2 : 0.5);
  const { ref: analyticsRef, meetsThreshold } = useFullyVisible<HTMLDivElement>({
    threshold: effectiveViewThreshold,
  });
  const hasTrackedRef = useRef(false);

  // Get the component for this section key
  const SectionComponent = sectionRegistry[key];

  // Some sections animate their internal layout; avoid double-animating the whole wrapper.
  const disableWrapperReveal =
    key === 'collectionSection' ||
    key === 'roundMenuImageSection' ||
    key === 'roundIconSection' ||
    key === 'clipCardsSection' ||
    key === 'spotlightSection' ||
    key === 'squareMenuSection';

  // Desktop-only "reveal on enter viewport" animation.
  // Keep SSR safe by defaulting to visible until mounted on client.
  const isDesktop = useIsDesktop();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [isRevealed, setIsRevealed] = useState(true);
  const hasEverIntersectedRef = useRef(false);

  // `useIsDesktop()` and `useMediaQuery()` already return false during SSR/initial hydration,
  // so we don't need an extra "isMounted" state (and our lint forbids setState in effects).
  const shouldAnimate = isDesktop && !prefersReducedMotion && !disableWrapperReveal;

  // Centralized "in view once" flag for section-specific animations (via Tailwind group-data variants).
  const animationThresholdByKey: Record<string, number> = {
    spotlightSection: 1.0,
    editorialSection: 0.2,
  };
  const animationThreshold = animationThresholdByKey[key] ?? 0.5;
  const [inViewOnce, setInViewOnce] = useState(false);
  const hasSetInViewOnceRef = useRef(false);

  const { ref: inViewRef } = useIntersectionObserver<HTMLDivElement>(
    {
      threshold: animationThreshold,
      rootMargin: '0px 0px -10% 0px',
      disconnectOnIntersect: true,
      enabled: isDesktop && !prefersReducedMotion,
    },
    (entry) => {
      if (!entry.isIntersecting) return;
      if (hasSetInViewOnceRef.current) return;
      hasSetInViewOnceRef.current = true;
      setInViewOnce(true);
    }
  );

  // Start observing only when we actually want desktop animations.
  const { ref: revealRef } = useIntersectionObserver<HTMLDivElement>(
    {
      threshold: 0.15,
      rootMargin: '0px 0px -10% 0px',
      disconnectOnIntersect: true,
      enabled: shouldAnimate,
    },
    (entry) => {
      // If it ever intersects, mark revealed and never hide again.
      if (entry.isIntersecting) {
        hasEverIntersectedRef.current = true;
        setIsRevealed(true);
        return;
      }

      // If it hasn't intersected yet, we can safely hide it (likely below the fold).
      // This avoids SSR/hydration blanking for above-the-fold content.
      if (!hasEverIntersectedRef.current) {
        setIsRevealed(false);
      }
    }
  );

  // When wrapper reveal is disabled, treat it as always revealed (no state updates needed).
  const effectiveIsRevealed = shouldAnimate ? isRevealed : true;

  const revealClassName = useMemo(() => {
    if (!shouldAnimate) return undefined;

    // Smooth "fade up" reveal similar to many e-commerce homepages.
    const base =
      'transform-gpu transition-[opacity,transform] duration-700 ease-out will-change-transform';
    const hidden = 'opacity-0 translate-y-6';
    const shown = 'opacity-100 translate-y-0';

    return `${base} ${effectiveIsRevealed ? shown : hidden}`;
  }, [effectiveIsRevealed, shouldAnimate]);

  // Track section viewed event - must be before any early returns
  useEffect(() => {
    if (!SectionComponent) return; // Skip if no component
    if (hasTrackedRef.current) return;
    if (!analytics || !isInitialized) return;
    if (!meetsThreshold) return;

    hasTrackedRef.current = true;
    try {
      analytics.trackEvent('Home Page Section Viewed', {
        'Section Name': eventLabel || key,
      });
    } catch (error) {
      console.error('Error tracking Home Page Section Viewed:', error);
    }
  }, [SectionComponent, analytics, isInitialized, key, eventLabel, meetsThreshold]);

  if (!SectionComponent) {
    // Silently skip sections that don't have components yet
    return null;
  }

  // Render the section component with its data
  return (
    <div
      ref={inViewRef}
      data-inview={inViewOnce ? 'true' : 'false'}
      className="group"
    >
      <div ref={revealRef} className={revealClassName}>
        <div ref={analyticsRef} data-analytics-section-name={eventLabel || key}>
          <SectionComponent
            data={value}
            sectionKey={key}
            eventLabel={eventLabel}
            priority={priority}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Render all sections from the home page data array
 */
interface RenderSectionsProps {
  sections: Array<{
    priority?: number;
    key: string;
    eventLabel?: string;
    value: Record<string, unknown>;
    showInDesktop?: boolean;
    showInMobile?: boolean;
    showInOldHome?: boolean; // If true, section is hidden (filtered out before render)
    layoutSideBySide?: boolean; // If true, this section will be rendered side by side with the next section
    pairWith?: string; // Optional: specify which section key to pair with (for non-adjacent sections)
    separatorColor?: string; // Optional: color for separator between side-by-side sections
    viewThreshold?: number; // 0-1, threshold for "Section Viewed" event (default: 1.0)
  }>;
}

export function RenderSections({ sections }: RenderSectionsProps) {
  // Sort by priority if needed (optional, similar to commented code in original)
  // const sortedSections = [...sections].sort((a, b) => (a.priority || 0) - (b.priority || 0));

  const renderedSections: React.ReactNode[] = [];
  const renderedIndices = new Set<number>(); // Track which sections have been rendered

  for (let i = 0; i < sections.length; i++) {
    // Skip if this section has already been rendered as part of a pair
    if (renderedIndices.has(i)) {
      continue;
    }

    const currentSection = sections[i];
    const showCurrentDesktop = currentSection.showInDesktop !== false;
    const showCurrentMobile = currentSection.showInMobile !== false;
    let pairedSection: typeof currentSection | null = null;
    let pairedIndex = -1;

    // Check if current section has layoutSideBySide enabled
    if (currentSection.layoutSideBySide) {
      // If pairWith is specified, find that section
      if (currentSection.pairWith) {
        pairedIndex = sections.findIndex(
          (s, idx) => s.key === currentSection.pairWith && idx > i && !renderedIndices.has(idx)
        );
        if (pairedIndex !== -1) {
          pairedSection = sections[pairedIndex];
        }
      } else {
        // Otherwise, use the next section
        const nextIndex = i + 1;
        if (nextIndex < sections.length && !renderedIndices.has(nextIndex)) {
          pairedSection = sections[nextIndex];
          pairedIndex = nextIndex;
        }
      }
    }

    if (pairedSection && pairedIndex !== -1) {
      const showPairedDesktop = pairedSection.showInDesktop !== false;
      const showPairedMobile = pairedSection.showInMobile !== false;
      // Get separator color from current section or paired section
      const separatorColor = currentSection.separatorColor || pairedSection.separatorColor;

      // If there are sections between current and paired, preserve the natural order on mobile.
      // Desktop still renders only the paired sections side-by-side.
      const betweenSections = sections.slice(i + 1, pairedIndex);
      const hasMobileContent =
        showCurrentMobile ||
        showPairedMobile ||
        betweenSections.some((section) => section.showInMobile !== false);
      const hasDesktopContent =
        showCurrentDesktop ||
        showPairedDesktop ||
        betweenSections.some((section) => section.showInDesktop !== false);

      if (!hasMobileContent && !hasDesktopContent) {
        renderedIndices.add(i);
        renderedIndices.add(pairedIndex);
        for (let j = i + 1; j < pairedIndex; j++) {
          renderedIndices.add(j);
        }
        continue;
      }
      
      // Render both sections side by side on desktop, but keep correct stacked order on mobile
      renderedSections.push(
        <div
          key={`combined-${i}-${pairedIndex}`}
        >
          {/* Mobile: render in natural order (current -> between -> paired) */}
          <div className="md:hidden">
            {showCurrentMobile && <SectionRenderer section={currentSection} />}
            {betweenSections
              .filter((section) => section.showInMobile !== false)
              .map((s, idx) => (
                <SectionRenderer key={`${s.key}-between-${i}-${idx}`} section={s} />
              ))}
            {showPairedMobile && <SectionRenderer section={pairedSection} />}
          </div>

          {/* Desktop: keep side-by-side pairing */}
          {(showCurrentDesktop || showPairedDesktop) && (
            <div className="hidden w-full md:flex md:items-start md:container-fluid py-4 md:py-6 overflow-hidden">
              {showCurrentDesktop && (
                <div
                  className={`w-full md:flex-shrink-0 md:flex md:flex-col ${
                    showPairedDesktop ? 'md:w-1/2 md:pr-2' : 'md:w-full'
                  }`}
                >
                  <SectionRenderer section={currentSection} />
                </div>
              )}
              {showCurrentDesktop && showPairedDesktop && separatorColor && (
                <div
                  className="hidden md:block w-px flex-shrink-0 mx-2 self-stretch"
                  style={{ backgroundColor: separatorColor, minHeight: '100%' }}
                />
              )}
              {showPairedDesktop && (
                <div
                  className={`w-full md:flex-shrink-0 md:flex md:flex-col ${
                    showCurrentDesktop ? 'md:w-1/2 md:pl-2' : 'md:w-full'
                  }`}
                >
                  <SectionRenderer section={pairedSection} />
                </div>
              )}
            </div>
          )}

          {/* Desktop: also render the in-between sections after the paired block (they're rendered inside the mobile stack above) */}
          {betweenSections.length > 0 && (
            <div className="hidden md:block">
              {betweenSections
                .filter((section) => section.showInDesktop !== false)
                .map((s, idx) => (
                  <SectionRenderer key={`${s.key}-between-desktop-${i}-${idx}`} section={s} />
                ))}
            </div>
          )}
        </div>
      );
      renderedIndices.add(i);
      renderedIndices.add(pairedIndex);
      for (let j = i + 1; j < pairedIndex; j++) {
        renderedIndices.add(j);
      }
    } else {
      // Render normally
      if (!showCurrentDesktop && !showCurrentMobile) {
        renderedIndices.add(i);
        continue;
      }
      const wrapperClassName = `${showCurrentDesktop ? '' : 'md:hidden'}${
        showCurrentMobile ? '' : ' hidden md:block'
      }`.trim();
      renderedSections.push(
        <div
          key={`${currentSection.key}-${i}`}
          className={wrapperClassName || undefined}
        >
          <SectionRenderer section={currentSection} />
        </div>
      );
      renderedIndices.add(i);
    }
  }

  return <>{renderedSections}</>;
}

